<template>
  <p class="spec-head">
    <span class="spec-head-index" aria-hidden="true">{{ pad }}</span>
    <span class="spec-head-body">
      <span class="spec-head-label">{{ label }}</span>
      <span v-if="note" class="spec-head-note">{{ note }}</span>
    </span>
  </p>
</template>

<style lang="scss" scoped>
@import '~/_variables.scss';

/* The head of one group of the case page's spec sheet: the group's place, its
   name, and the sentence that says what the group is.

   It exists because those groups had no head at all. Each was a bare uppercase
   label with its content under it, which on a band where every other element is
   an object - a dark slab, a card, a panel, a framed screen, a grid of tiles -
   left the sheet's four quarters as the only place where the page simply stopped
   arranging things.

   It was a ruled line until the note arrived, and the two are alternatives rather
   than companions. The line had four parts: the number, the name, a rule and the
   tally of what the group held. With a sentence under the name, the rule became a
   second horizontal line competing with the plate's own cross-ruling a few pixels
   away, and the tally became a number restating what the reader can count by
   looking at the group. A name, a note and the thing itself is the whole head.

   The note is what the head is for. A group called "Tools" over three marks is a
   caption that asks the reader to work out what the group means; a sentence under
   it says what the three marks are, which is the only part of the quarter that
   the marks and their names cannot state themselves. It is a note rather than a
   lede because a lede opens an argument and this only names a subject.

   The note takes $text-body rather than $text-muted, and it is the same reason
   the tally took $label-on-light: at this size $text-muted measures 2.7:1 on
   white, and a sentence nobody can read is worse than a loud one. It is a step
   under the body's own 1rem so that four notes on one band do not read as four
   paragraphs, and it carries no tracking and no capitalisation, which is what
   keeps it prose beside an uppercase name at nearly the same size.

   There is no surface here, and that is a decision rather than an omission. The
   four groups are the one part of the page that states its specs flat: the band
   already opens on an ink slab and the marks below already sit on their own
   tiles, so a board around a group would double a surface inside the band and a
   tile inside it. A sheet of rules is also the idiom that fits: a card says
   "here is an object", and a specification is not an object, it is a line of
   data.

   The number is the group's place in the sheet, and it is set in the name's own
   ink on purpose. It was ghosted at $line-strong while it was the ornament on a
   ruled line; with the rule gone it is part of a two-line block, and a figure
   fading into the background beside the word it belongs to reads as a smudge
   rather than as a mark. What separates it from the name is the size and the
   tick, which is the same pair that separates it in the story's chapter rail one
   band down: the page writes this number one way, and this is that way.

   It is padded to two digits, so that ten groups would not shift the name
   column.

   It names no tone and no size. It is the light band's head and the case page is
   the only reader, so a dark variant would be a second component rather than a
   prop, and it would be written the day a dark band needs a spec sheet. */
/* The head's step under itself is the page's step under a numbered head, which
   is the story chapter rail's 1.5rem: the two are the same object at the sheet's
   scale rather than a section's, so the air between the head and what it
   introduces is the same on both bands. It was 0.9rem while the head was a single
   ruled line and the air was a label's; a three-line head with a sentence in it
   is a block, and a block needs the larger step or the note reads as part of the
   marks under it. */
.spec-head {
  align-items: stretch;
  display: flex;
  gap: 0.7rem;
  margin: 0 0 1.5rem;
}

/* The rail. Its border is the one thing left of the rule the head used to draw,
   and it stretches the height of the two-line block because a flex item does:
   that is what makes the number read as a margin note beside the name rather than
   as a character that happens to sit before it.

   The number sits in the middle of that height rather than on the block's first
   line. The rail is two lines tall, and a figure aligned to the top belongs to
   the name alone; centred, it belongs to the whole block, which is what a note
   in the margin does. The centring is declared here rather than as
   `align-items: center` on the head above, because the head's stretch is what
   draws the full-height border: the element centres its own text, so the figure
   moves and the rail does not. That makes this element a flex container with one
   anonymous item, which is exactly the text it holds.

   It is set a step over the name, and that pair is what separates the two now
   that they share an ink: at one size, a figure and a word in one colour read as
   two words. The step is the page's own idiom for a rail numeral read down a
   band, which the story's chapter heads establish at 2.6rem against a section
   head; this is the same mark at the scale of a head that is two lines rather
   than a section's four. */
.spec-head-index {
  align-items: center;
  border-right: 1px solid $line-blue;
  color: $label-on-light;
  display: flex;
  flex: 0 0 auto;
  font-family: $headings-font-family;
  font-size: 1.5rem;
  font-weight: 700;
  letter-spacing: -0.02em;
  line-height: 1;
  padding-right: 0.7rem;
}

.spec-head-body {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  min-width: 0;
}

.spec-head-label {
  color: $label-on-light;
  font-family: $headings-font-family;
  font-size: $label-size;
  font-weight: $label-weight;
  letter-spacing: $label-tracking;
  text-transform: uppercase;
}

.spec-head-note {
  color: $text-body;
  font-size: 0.85rem;
  line-height: 1.45;
}
</style>

<script>
export default {
  props: {
    /* The group's place in the sheet, as the number it is: the head pads it to
       two digits itself rather than taking a string, so the page states which
       group it is and the component owns how that is written. */
    index: {
      type: Number,
      required: true
    },
    /* The group's name. It is the page's translated label, the way a section
       title's text is, because what the group is called is the page's copy. */
    label: {
      type: String,
      required: true
    },
    /* What the group is, as one short sentence under the name. An empty string
       drops it, which leaves the head a name and a number: a group with nothing
       to explain is a group whose name is enough. */
    note: {
      type: String,
      default: ''
    }
  },

  computed: {
    pad() {
      return String(this.index).padStart(2, '0');
    }
  }
};
</script>
